import {
    createAsyncThunk,
    createSlice,
    isAnyOf,
    PayloadAction,
} from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { AppState } from '../../reducers/root.reducer';
import { RequestServiceResponse } from '@tcc-online/request-service';
import UploadState from '../../types/UploadState';
import Participant360 from '../types/project/360/Participant360';
import Admin360ParticipantService, {
    ParticipantPayload,
    CreateEvaluatorPayload,
    EditEvaluatorPayload,
    EvaluatorPayload,
} from '../services/Admin360ParticipantService';
import Evaluator from '../types/project/360/Evaluator';

const NAME = 'Admin360Participant';

const getParticipants = createAsyncThunk(
    `${NAME}/getParticipants`,
    Admin360ParticipantService.getParticipants,
);
const getParticipant = createAsyncThunk(
    `${NAME}/getParticipant`,
    Admin360ParticipantService.getParticipant,
);
const createParticipant = createAsyncThunk(
    `${NAME}/createParticipant`,
    Admin360ParticipantService.createParticipant,
);
const deleteParticipant = createAsyncThunk(
    `${NAME}/deleteParticipant`,
    Admin360ParticipantService.deleteParticipant,
);

const getEvaluators = createAsyncThunk(
    `${NAME}/getEvaluators`,
    Admin360ParticipantService.getEvaluators,
);
const getEvaluator = createAsyncThunk(
    `${NAME}/getEvaluator`,
    Admin360ParticipantService.getEvaluator,
);
const createEvaluator = createAsyncThunk(
    `${NAME}/createEvaluator`,
    Admin360ParticipantService.createEvaluator,
);
const editEvaluator = createAsyncThunk(
    `${NAME}/editEvaluator`,
    Admin360ParticipantService.editEvaluator,
);
const deleteEvaluator = createAsyncThunk(
    `${NAME}/deleteEvaluator`,
    Admin360ParticipantService.deleteEvaluator,
);

export interface Admin360ParticipantState {
    list: UploadState<Participant360[]>;
}

const initialState: Admin360ParticipantState = {
    list: {
        data: [],
        loading: false,
        uploading: false,
    },
};

const slice = createSlice({
    name: NAME,
    initialState,
    reducers: {},
    extraReducers: (builder): void => {
        builder.addCase(
            getParticipants.pending,
            (state: Admin360ParticipantState): void => {
                state.list.loading = true;
            },
        );
        builder.addCase(
            getParticipants.fulfilled,
            (
                state: Admin360ParticipantState,
                action: PayloadAction<RequestServiceResponse<Participant360[]>>,
            ): void => {
                state.list.data = action.payload.data;
            },
        );

        builder.addCase(
            createParticipant.pending,
            (state: Admin360ParticipantState): void => {
                state.list.loading = true;
            },
        );
        builder.addCase(
            createParticipant.fulfilled,
            (
                state: Admin360ParticipantState,
                action: PayloadAction<RequestServiceResponse<Participant360>>,
            ): void => {
                if (state.list.data?.length >= 0) {
                    state.list.data.push(action.payload.data);
                }
            },
        );

        builder.addCase(
            editEvaluator.fulfilled,
            (
                state: Admin360ParticipantState,
                action: PayloadAction<
                    RequestServiceResponse<Evaluator>,
                    string,
                    { arg: EditEvaluatorPayload & EvaluatorPayload }
                >,
            ): void => {
                const participants: Participant360[] = state.list.data;

                const participantIndex: number | null =
                    findParticipantIndexById(
                        participants,
                        action.meta.arg.participantId,
                    );
                if (participantIndex === null) {
                    return;
                }

                const evaluatorIndex: number = findEvaluatorIndex(
                    participants[participantIndex].evaluators,
                    action.payload.data?.id,
                );

                if (evaluatorIndex >= 0) {
                    state.list.data[participantIndex].evaluators[
                        evaluatorIndex
                    ] = action.payload.data;
                }
            },
        );

        builder.addCase(
            createEvaluator.fulfilled,
            (
                state: Admin360ParticipantState,
                action: PayloadAction<
                    RequestServiceResponse<Evaluator>,
                    string,
                    { arg: CreateEvaluatorPayload & ParticipantPayload }
                >,
            ): void => {
                const participants: Participant360[] = state.list.data;
                const participantIndex: number | null =
                    findParticipantIndexById(
                        participants,
                        action.meta.arg.participantId,
                    );
                if (participantIndex === null) {
                    return;
                }

                const evaluators: Evaluator[] =
                    participants[participantIndex].evaluators;
                if (!evaluators) {
                    state.list.data[participantIndex].evaluators = [];
                }

                state.list.data[participantIndex].evaluators.push(
                    action.payload.data,
                );
            },
        );

        builder.addCase(
            deleteEvaluator.fulfilled,
            (
                state: Admin360ParticipantState,
                action: PayloadAction<
                    RequestServiceResponse,
                    string,
                    { arg: EvaluatorPayload }
                >,
            ): void => {
                const participants: Participant360[] = state.list.data;
                const participantIndex: number | null =
                    findParticipantIndexById(
                        participants,
                        action.meta.arg.participantId,
                    );
                if (participantIndex === null) {
                    return;
                }

                const evaluatorIndex: number = findEvaluatorIndex(
                    participants[participantIndex].evaluators,
                    action.meta.arg.evaluatorId,
                );
                if (evaluatorIndex >= 0) {
                    state.list.data[participantIndex].evaluators.splice(
                        evaluatorIndex,
                        1,
                    );
                }
            },
        );

        builder.addCase(
            deleteParticipant.fulfilled,
            (state: Admin360ParticipantState, action): void => {
                if (!state.list.data) {
                    return;
                }

                const index: number = state.list.data.findIndex(
                    (participant: Participant360) =>
                        participant.id === action.meta.arg.participantId,
                );
                if (index >= 0) {
                    state.list.data.splice(index, 1);
                }
            },
        );

        builder.addMatcher(
            isAnyOf(getParticipants.pending, getParticipant.pending),
            (state: Admin360ParticipantState): void => {
                state.list.loading = true;
            },
        );

        builder.addMatcher(
            isAnyOf(
                getParticipants.fulfilled,
                getParticipants.rejected,
                getParticipant.fulfilled,
                getParticipant.rejected,
            ),
            (state: Admin360ParticipantState): void => {
                state.list.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(createParticipant.pending, deleteParticipant.pending),
            (state: Admin360ParticipantState): void => {
                state.list.uploading = true;
            },
        );

        builder.addMatcher(
            isAnyOf(
                createParticipant.fulfilled,
                createParticipant.rejected,
                deleteParticipant.fulfilled,
                deleteParticipant.rejected,
            ),
            (state: Admin360ParticipantState): void => {
                state.list.uploading = false;
            },
        );
    },
});

const state = (state: AppState): Admin360ParticipantState =>
    state.admin['360'].participant;
const participants = createSelector(
    state,
    (state: Admin360ParticipantState) => state.list,
);

interface Filters {
    text?: string;
    relationship?: string;
}

const filters = (_: AppState, filters: Filters) => filters;

const Admin360ParticipantSlice = {
    reducer: slice.reducer,
    actions: {
        getParticipants,
        getParticipant,
        createParticipant,
        deleteParticipant,
        getEvaluators,
        getEvaluator,
        createEvaluator,
        editEvaluator,
        deleteEvaluator,
    },
    state: {
        all: createSelector(state, (state: Admin360ParticipantState) => state),
        participants,
        filteredParticipants: createSelector(
            [participants, filters],
            (participants: UploadState<Participant360[]>, filters: Filters) => {
                if (
                    (!filters.text || filters.text.trim() == '') &&
                    !filters.relationship
                ) {
                    return participants.data;
                }
                const filtered: Participant360[] = [];

                for (const participant of participants.data) {
                    const filteredEvaluators: Evaluator[] =
                        participant.evaluators.filter(filter(filters));

                    if (filteredEvaluators.length > 0) {
                        const newParticipant = {
                            ...participant,
                            evaluators: filteredEvaluators,
                        };
                        filtered.push(newParticipant);
                    }
                }

                return filtered;

                function filter(filters: Filters) {
                    return (evaluator: Evaluator) => {
                        const { text, relationship } = filters;
                        const name: string =
                            evaluator.email +
                            evaluator.lastName +
                            evaluator.firstName;
                        let regexp: RegExp | undefined;
                        if (text && text.trim() != '') {
                            regexp = new RegExp(
                                text.replace(/[{}()|[\]\\]/g, ''),
                                'i',
                            );
                        }

                        if (!regexp) {
                            return evaluator.relationship === relationship;
                        }

                        const test = regexp.test(name);
                        if (!relationship) {
                            return test;
                        }

                        return test && evaluator.relationship === relationship;
                    };
                }
            },
        ),
    },
};

export default Admin360ParticipantSlice;

function findParticipantIndexById(
    participants: Participant360[],
    participantId: number,
): number | null {
    if (!participants || participants.length === 0) {
        return null;
    }

    const participantIndex: number = participants.findIndex(
        (participant: Participant360) => participant.id === participantId,
    );
    return participantIndex >= 0 ? participantIndex : null;
}

function findEvaluatorIndex(
    evaluators: Evaluator[],
    evaluatorId: number,
): number {
    if (!evaluators || evaluators.length === 0) {
        return -1;
    }

    return evaluators.findIndex(
        (evaluator: Evaluator) => evaluator.id === evaluatorId,
    );
}
